/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Divider, Form, Input, Radio, Row, Select, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import CustomInputNumber from '../../../Forms/Fields/CustomInputNumber';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { HTTP_REQUEST_CONTENT_TYPES } from '../../../../constants/main';
import { isJson } from '../../../../utils/is/isJson';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { VARIABLE_TYPES } from '../../../../constants/main';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import { URL_REGEX } from '../../../../constants/regex';

// Constant declarations
const { Option } = Select;
const { Text } = Typography;

const HttpRequestNodeForm = ({ form }) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage } = useBotLanguage();

  const { data: storyVariables, isFetching: isFetchingStoryVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId });

  const [bodyResponseVariables, setBodyResponseVariables] = useState([]);
  const [responseHeadersVariables, setResponseHeadersVariables] = useState([]);
  const [responseStatusCodeVariables, setResponseStatusCodeVariables] = useState([]);
  const [sessionVariables, setSessionVariables] = useState([]);
  const [contactVariables, setContactVariables] = useState([]);
  const [staticVariables, setStaticVariables] = useState([]);
  const [showConflictMessage, setShowConflictMessage] = useState(false);

  useEffect(() => {
    const _sessionVariables = storyVariables.filter(variable => variable.type === 'SESSION');
    setSessionVariables(_sessionVariables);
    const _contactVariables = storyVariables.filter(variable => variable.type === 'CONTACT');
    setContactVariables(_contactVariables);
    const _staticVariables = storyVariables.filter(variable => variable.type === 'STATIC' && !variable.isConstant);
    setStaticVariables(_staticVariables);

    const validBodyResponseVariables = [..._sessionVariables, ..._contactVariables];
    const bodyMappings = form.getFieldValue(['data', 'httpRequestData', 'mappings']) || [];
    for (const entry of bodyMappings) {
      if (!validBodyResponseVariables.find(variable => variable.name === entry.variable)) {
        validBodyResponseVariables.push(entry);
      }
    }
    setBodyResponseVariables(validBodyResponseVariables); // session variables + contact variables + body response variables

    const validResponseHeadersVariables = [..._sessionVariables];
    const responseHeadersVarsMappings = form.getFieldValue(['data', 'httpRequestData', 'responseHeaderMappings']) || [];
    for (const entry of responseHeadersVarsMappings) {
      if (!validResponseHeadersVariables.find(variable => variable.name === entry.variable)) {
        validResponseHeadersVariables.push(entry);
      }
    }
    setResponseHeadersVariables(validResponseHeadersVariables); // session variables + response header variables

    const validResponseStatusCodeVariables = [..._sessionVariables];
    const responseStatusCode = form.getFieldValue(['data', 'httpRequestData', 'responseStatusCodeMapping']);

    if (responseStatusCode && !validResponseStatusCodeVariables.find(variable => variable.name === responseStatusCode)) {
      validResponseStatusCodeVariables.push({ name: responseStatusCode, type: '' });
    }

    setResponseStatusCodeVariables(validResponseStatusCodeVariables); // session variables + response status code variables
  }, [storyVariables]);

  const updateResponseBodyVariables = (newVariableName) => {
    if (newVariableName && storyVariables.filter(variable => variable.name === newVariableName).length === 0) {
      setBodyResponseVariables([...new Set([...sessionVariables, ...contactVariables, ...staticVariables, { name: newVariableName, type: '' }])]); // session variables + contact variables + non constant static variables + body response variables
    }
  };

  const updateResponseHeadersVariables = (newVariableName) => {
    if (newVariableName && storyVariables.filter(variable => variable.name === newVariableName).length === 0) {
      setResponseHeadersVariables([...new Set([...sessionVariables, { name: newVariableName, type: '' }])]); // session variables + response headers variables
    }
  };

  const updateResponseStatusCodeVariables = (newVariableName) => {
    if (newVariableName && storyVariables.filter(variable => variable.name === newVariableName).length === 0) {
      setResponseStatusCodeVariables([...new Set([...sessionVariables, { name: newVariableName, type: '' }])]); // session variables + response status code variables
    }
  };

  const HttpMethodsDropdown = () => {
    return(
      <Form.Item
        name={['data', 'httpRequestData', 'method']}
        className="m-0"
      >
        <Select
          style={{ width: 98 }}
          className="mx-0 text-left http-request-method-select"
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          disabled={!isEditingPrimaryLanguage}
          onChange={(value) => {
            /*
            * Because this form item is a child component of another
            * form item we have to manually set the parent form item
            * as 'touched' to allow users to save their changes
            */
            form.setFields([
              { name: ['data', 'httpRequestData', 'url'], touched: true }
            ]);
            // We should not allow a 'GET' request with a payload
            if (value === 'GET') {
              form.setFieldValue(['data', 'httpRequestData', 'body'], null);
            }
          }}
        >
          <Option value="GET"/>
          <Option value="POST"/>
          <Option value="PATCH"/>
          <Option value="PUT"/>
          <Option value="DELETE"/>
        </Select>
      </Form.Item>
    );
  };

  const onUrlChange = (url) => {
    try {
      const urlSearchParams = new URL(url).searchParams;
      if (!urlSearchParams.size) {
        return form.setFieldValue(['data', 'httpRequestData', 'urlSearchParams'], []);

      }

      const paramArray = [];
      for (const [key, value] of urlSearchParams) {
        paramArray.push({ key, value });
      }
      form.setFieldValue(['data', 'httpRequestData', 'urlSearchParams'], paramArray);
    } catch (_e) {}
  };

  const onUrlSearchParamChange = () => {
    const updatedUrlSearchParams = new URLSearchParams();
    const formValues = form.getFieldValue(['data', 'httpRequestData', 'urlSearchParams']);

    for (const item of formValues) {
      if (item?.key && item?.value) {
        updatedUrlSearchParams.append(item.key, item.value);
      }
    }

    try {
      const url = new URL(form.getFieldValue(['data', 'httpRequestData', 'url']));
      if (updatedUrlSearchParams.size) {
        form.setFieldValue(['data', 'httpRequestData', 'url'], url.origin + url.pathname + '?' + updatedUrlSearchParams.toString());
      } else {
        form.setFieldValue(['data', 'httpRequestData', 'url'], url.origin + url.pathname);
      }
    } catch (_e) {}
  };

  return (
    <Fragment>

      <Row gutter={16} className="mb-4">
        <Col xs={24}>
          <Form.Item
            className="mb-1"
            label= {t('Friendly name')}
            name={['data', 'friendlyName']}
          >
            <Input
              placeholder={t('Enter a short friendly name')}
              size="large"
              maxLength={30}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" className="mb-6 mt-0">
        <Text type="secondary" className="fs-base">{t('HTTP Request')}</Text>
      </Divider>
      <Row gutter={16}>
        <Col xs={24}>
          <Form.Item
            label={t('URL')}
            name={['data', 'httpRequestData', 'url']}
            rules={[
              {
                required: true,
                message: t('URL cannot be blank.')
              },
              {
                validator: (_rule, value) => {
                  // Skip validation if no value provided
                  if (!value) {
                    setShowConflictMessage(false);
                    return Promise.resolve();
                  }

                  const isValidUrl = URL_REGEX.test(value);
                  setShowConflictMessage(!isValidUrl);

                  return isValidUrl ? Promise.resolve() : Promise.reject();
                },
                warningOnly: true,
                message: t('The URL may not be valid')
              }]}
            validateStatus={showConflictMessage ? 'warning' : undefined}
          >
            <RichTextEditor
              toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
              placeholder={t('Enter request url')}
              hasFloatingToolbar={true}
              handleAsPlainText={true}
              handleInsertedTextWithoutFormatting={true}
              elementStyles={{ p: { marginBottom: 0 } }}
              onKeyDown={(e) => {
                if (e.key === 'Spacebar' || e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onChange={(value) => onUrlChange(value)}
              disabled={!isEditingPrimaryLanguage}
              addonAfter={<HttpMethodsDropdown />}
              inline={true}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24}>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                className="mb-3"
                label={
                  <Typography.Text>
                    {t('Body')}
                    <Tooltip
                      getPopupContainer={(triggerNode) => triggerNode.parentNode.parentNode.parentNode.parentNode}
                      title={t('You can add variables to the request body with "{{...}}"')}
                    >
                      <InfoCircleOutlined className="ml-2"/>
                    </Tooltip>
                  </Typography.Text>
                }
                name={['data', 'httpRequestData', 'contentType']}
                initialValue={HTTP_REQUEST_CONTENT_TYPES.JSON.key}
              >
                <Radio.Group
                  disabled={!isEditingPrimaryLanguage || form.getFieldValue(['data', 'httpRequestData', 'method']) === 'GET'}
                  name="radiogroup"
                  defaultValue={1}
                >
                  {
                    Object.values(HTTP_REQUEST_CONTENT_TYPES).map(type => (<Radio value={type.key}>{type.label}</Radio>))
                  }
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            shouldUpdate={(prevValues, currentValues) => {
              return prevValues?.data?.httpRequestData?.contentType !== currentValues?.data?.httpRequestData?.contentType;
            }}
            noStyle
          >
            {({ getFieldValue }) => {
              const contentType = getFieldValue(['data', 'httpRequestData', 'contentType']);
              const isJsonType = contentType === HTTP_REQUEST_CONTENT_TYPES.JSON.key;

              return isJsonType ? (
                <Form.Item
                  name={['data', 'httpRequestData', 'body']}
                  rules={[
                    {
                      validator: (_rule, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        if ((!isJson(value))) {
                          return Promise.reject();
                        }
                        return Promise.resolve();
                      },
                      message: t('Please enter a valid JSON body')
                    }
                  ]}
                >
                  <RichTextEditor
                    toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
                    placeholder={t('Enter request body')}
                    hasFloatingToolbar={true}
                    handleAsPlainText={true}
                    disabled={!isEditingPrimaryLanguage || form.getFieldValue(['data', 'httpRequestData', 'method']) === 'GET'}
                    elementStyles={{ p: { marginBottom: 0 } }}
                  />
                </Form.Item>
              ) : (
                <Form.List name={['data', 'httpRequestData', 'formData']}>
                  {(fields, { add, remove }, { errors }) => (
                    <Fragment>
                      <div className="mb-3">
                        {fields.map(({ key, name, ...restField }, index) => (
                          <Form.Item
                            key={key}
                            className="mb-4"
                          >
                            <Row wrap={false} gutter={8} align="bottom">
                              <Col flex="auto">
                                <Row gutter={16}>
                                  <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'key']}
                                      className="mb-0 hide-error"
                                    >
                                      <Input
                                        placeholder={t('Enter key')}
                                        size="large"
                                        disabled={!isEditingPrimaryLanguage || form.getFieldValue(['data', 'httpRequestData', 'method']) === 'GET'}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col sm={12} xs={24}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'value']}
                                      className="mb-0 hide-error"
                                    >
                                      <RichTextEditor
                                        toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
                                        placeholder={t('Enter value')}
                                        className="ql-sm ql-border-md ql-border-focus ql-font-normal"
                                        inline
                                        hasFloatingToolbar={true}
                                        handleAsPlainText={true}
                                        disabled={!isEditingPrimaryLanguage || form.getFieldValue(['data', 'httpRequestData', 'method']) === 'GET'}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Col>
                              <Col flex="none">
                                <Button
                                  icon={<DeleteIcon />}
                                  size="large"
                                  type="text"
                                  onClick={() => remove(name)}
                                  disabled={!isEditingPrimaryLanguage}
                                />
                              </Col>
                            </Row>
                          </Form.Item>
                        ))}
                      </div>
                      <Row className="mb-2" gutter={16}>
                        <Col sm={12} xs={24}>
                          <Button
                            type="dashed"
                            size="large"
                            block
                            onClick={() => add()}
                            icon={<PlusIcon />}
                            disabled={!isEditingPrimaryLanguage || form.getFieldValue(['data', 'httpRequestData', 'method']) === 'GET'}
                          >
                            {t('Add form field')}
                          </Button>
                        </Col>
                      </Row>
                      <Form.ErrorList errors={errors} />
                    </Fragment>
                  )}
                </Form.List>
              );
            }}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col xs={24}>
          <Typography.Text>{t('Query Params')}</Typography.Text>
          <Form.List
            name={['data', 'httpRequestData', 'urlSearchParams']}
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                <div className="mb-3">
                  {
                    fields.map(({ key, name, ...restField }, _index) => (
                      <Form.Item
                        key={key}
                        className="mb-4"
                      >
                        <Row wrap={false} gutter={8} align="bottom">
                          <Col flex="auto">
                            <Row gutter={16}>
                              <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'key']}
                                  className="mb-0 hide-error"

                                >
                                  <Input
                                    placeholder={t('Enter key')}
                                    size="large"
                                    disabled={!isEditingPrimaryLanguage}
                                    onBlur={onUrlSearchParamChange}
                                  />
                                </Form.Item>
                              </Col>

                              <Col sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'value']}
                                  className="mb-0 hide-error"
                                >
                                  <Input
                                    placeholder={t('Enter value')}
                                    size="large"
                                    disabled={!isEditingPrimaryLanguage}
                                    onBlur={onUrlSearchParamChange}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col flex="none">
                            <Button
                              icon={<DeleteIcon />}
                              size="large"
                              type="text"
                              onClick={() => {
                                remove(name);
                                onUrlSearchParamChange(name);
                              }}
                              disabled={!isEditingPrimaryLanguage}
                            />
                          </Col>
                        </Row>
                      </Form.Item>
                    ))
                  }
                </div>

                <Row gutter={16}>
                  <Col sm={12} xs={24}>
                    <Button
                      type="dashed"
                      size="large"
                      block
                      onClick={() => add()}
                      icon={<PlusIcon />}
                      disabled={!isEditingPrimaryLanguage}
                    >
                      {t('Add new URL parameter')}
                    </Button>
                  </Col>
                </Row>

                <Form.ErrorList errors={errors} />
              </Fragment>
            )}
          </Form.List>
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col xs={24}>
          <Typography.Text>{t('Headers')}</Typography.Text>
          <Form.List
            name={['data', 'httpRequestData', 'headers']}
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                <div className="mb-3">
                  {
                    fields.map(({ key, name, ...restField }, _index) => (
                      <Form.Item
                        key={key}
                        className="mb-4"
                      >
                        <Row wrap={false} gutter={8} align="bottom">
                          <Col flex="auto">
                            <Row gutter={16}>
                              <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'key']}
                                  className="mb-0 hide-error"
                                >
                                  <Input
                                    placeholder={t('Enter key')}
                                    size="large"
                                    disabled={!isEditingPrimaryLanguage}
                                  />
                                </Form.Item>
                              </Col>

                              <Col sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'value']}
                                  className="mb-0 hide-error"
                                >
                                  <Input
                                    placeholder={t('Enter value')}
                                    size="large"
                                    disabled={!isEditingPrimaryLanguage}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col flex="none">
                            <Button
                              icon={<DeleteIcon />}
                              size="large"
                              type="text"
                              onClick={() => remove(name)}
                              disabled={!isEditingPrimaryLanguage}
                            />
                          </Col>
                        </Row>
                      </Form.Item>
                    ))
                  }
                </div>

                <Row gutter={16}>
                  <Col sm={12} xs={24}>
                    <Button
                      type="dashed"
                      size="large"
                      block
                      onClick={() => add()}
                      icon={<PlusIcon />}
                      disabled={!isEditingPrimaryLanguage}
                    >
                      {t('Add new header')}
                    </Button>
                  </Col>
                </Row>

                <Form.ErrorList errors={errors} />
              </Fragment>
            )}
          </Form.List>
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('Retries')}
            name={['data', 'httpRequestData', 'retries']}
            tooltip={{
              title: (
                <Typography.Text type="secondary">{t('The number of times the request will be performed again in case of failure.')}</Typography.Text>
              ),
              icon: <InfoCircleOutlined />
            }}
            onKeyPress={(e) => {
              if (e.key === '.') {
                e.preventDefault(); // Prevent non-numeric keys
              }
            }}
          >
            <CustomInputNumber
              placeholder="-"
              defaultValue={0}
              min={0}
              max={5}
              size="large"
              step={1}
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            initialValue={5}
            label={t('Timeout (seconds)')}
            name={['data', 'httpRequestData', 'timeout']}
            tooltip={{
              title: (
                <Typography.Text type="secondary">{t('The maximum duration in seconds, to wait for a response from the request. If not set ,the request will wait for 10 seconds for the server to respond.')}</Typography.Text>
              ),
              icon: <InfoCircleOutlined />
            }}
            onKeyPress={(e) => {
              if (e.key === '.') {
                e.preventDefault(); // Prevent non-numeric keys
              }
            }}
          >
            <CustomInputNumber
              placeholder="-"
              min={1}
              max={30}
              size="large"
              step={5}
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" className="mb-6 mt-0">
        <Text type="secondary" className="fs-base">{t('HTTP Response')}</Text>
      </Divider>

      <Row gutter={16} className="mb-4">
        <Col xs={24}>
          <Typography.Text>
            {t('Body Variable Mapping')}
            <Tooltip
              getPopupContainer={(triggerNode) => triggerNode.parentNode.parentNode.parentNode.parentNode}
              title={t('You can map the response properties to existing variables')}
            >
              <InfoCircleOutlined className="ml-2"/>
            </Tooltip>
          </Typography.Text>
          <Form.List
            name={['data', 'httpRequestData', 'mappings']}
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                <div className="mb-3">
                  {
                    fields.map(({ key, name, ...restField }, _index) => (
                      <Form.Item
                        key={key}
                        className="mb-4"
                      >
                        <Row wrap={false} gutter={8} align="bottom">
                          <Col flex="auto">
                            <Row gutter={16}>
                              <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'path']}
                                  className="mb-0 hide-error"
                                >
                                  <Input
                                    placeholder={t('JSON pathname')}
                                    size="large"
                                    onKeyDown={(e) => {
                                      if (e.key === ' ') {
                                        e.preventDefault();
                                      }
                                    }}
                                    disabled={!isEditingPrimaryLanguage}
                                  />
                                </Form.Item>
                              </Col>

                              <Col sm={12} xs={24}>
                                <Form.Item className="m-0">
                                  <Row wrap={false} align="bottom" className="w-100">
                                    <Col flex={'auto'}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'variable']}
                                        className="mb-0 hide-error"
                                      >
                                        {
                                          storyVariables ? (
                                            <Select
                                              showSearch
                                              size="large"
                                              disabled={!isEditingPrimaryLanguage}
                                              placeholder={t('Set to variable')}
                                              getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                              // When the user types a new variable name, add it to the list of local variables
                                              onSearch={updateResponseBodyVariables}
                                              onBlur={(e) => updateResponseBodyVariables(e.target.value)}
                                              onSelect={(value) => {
                                                const selectedVariableData = bodyResponseVariables.find(variable => variable.name === value);
                                                if (selectedVariableData?.type) {
                                                  form.setFieldValue(['data', 'httpRequestData', 'mappings', name, 'type'], selectedVariableData.type);
                                                }
                                              }}
                                              loading={isFetchingStoryVariables}
                                              options={(bodyResponseVariables || [])
                                                .filter((variable) => {
                                                  const selectedVariables = form.getFieldValue(['data', 'httpRequestData', 'mappings']).map(entry => entry.variable);
                                                  const variableIdentifier = variable.name ?? variable.variable;

                                                  return variable.type !== VARIABLE_TYPES.CONTACT && variable.type !== VARIABLE_TYPES.QUESTION && variable.type !== VARIABLE_TYPES.CSAT && !selectedVariables.includes(variableIdentifier);
                                                })
                                                .map((element) => {
                                                  return {
                                                    value: element.name || element.variable,
                                                    label: element.name
                                                  };
                                                })
                                                // Keep only unique values
                                                .filter((elem, index, self) => self.findIndex((t) => {
                                                  return (t.value === elem.value);
                                                }) === index)
                                              }
                                            />
                                          ) : null
                                        }
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col flex="none">
                            <Button
                              icon={<DeleteIcon />}
                              size="large"
                              type="text"
                              onClick={() => remove(name)}
                              disabled={!isEditingPrimaryLanguage}
                            />
                          </Col>
                        </Row>
                      </Form.Item>
                    ))
                  }
                </div>

                <Row gutter={16}>
                  <Col sm={12} xs={24}>
                    <Button
                      type="dashed"
                      size="large"
                      block
                      onClick={() => add({ path: '', variable: undefined, type: 'SESSION' })}
                      icon={<PlusIcon />}
                      disabled={!isEditingPrimaryLanguage}
                    >
                      {t('Add new mapping')}
                    </Button>
                  </Col>
                </Row>

                <Form.ErrorList errors={errors} />
              </Fragment>
            )}

          </Form.List>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24}>
          {/* Status Code Variable */}
          <Form.Item
            label={t('Status Code Variable')}
            name={['data', 'httpRequestData', 'responseStatusCodeMapping']}
            className="hide-error"
          >
            {
              storyVariables && responseStatusCodeVariables ? (
                <Select
                  showSearch
                  size="large"
                  disabled={!isEditingPrimaryLanguage}
                  placeholder={t('Set to variable')}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  // When the user types a new variable name, add it to the list of local variables
                  onSearch={updateResponseStatusCodeVariables}
                  onBlur={(e) => updateResponseStatusCodeVariables(e.target.value)}

                  loading={isFetchingStoryVariables}
                  options={(responseStatusCodeVariables || [])
                    .filter((variable) => {
                      return ![VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.CSAT].includes(variable.type);
                    })
                    .map((element) => {
                      const variableObj = {
                        value: element.name || element.variable,
                        label: element.name
                      };
                      return variableObj;
                    })
                    // Keep only unique values
                    .filter((elem, index, self) => self.findIndex((t) => {
                      return (t.value === elem.value);
                    }) === index)
                  }
                />
              ) : null
            }
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16} className="mb-4">
        <Col xs={24}>
          <Typography.Text>
            {t('Headers Variable Mapping')}
            <Tooltip
              getPopupContainer={(triggerNode) => triggerNode.parentNode.parentNode.parentNode.parentNode}
              title={t('You can map the response headers to existing variables')}
            >
              <InfoCircleOutlined className="ml-2"/>
            </Tooltip>
          </Typography.Text>
          <Form.List
            name={['data', 'httpRequestData', 'responseHeaderMappings']}
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                <div className="mb-3">
                  {
                    fields.map(({ key, name, ...restField }, _index) => (
                      <Form.Item
                        key={key}
                        className="mb-4"
                      >
                        <Row wrap={false} gutter={8} align="bottom">
                          <Col flex="auto">
                            <Row gutter={16}>
                              <Col className="d-flex flex-column justify-content-end" sm={12} xs={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'path']}
                                  className="mb-0 hide-error"
                                >
                                  <Input
                                    placeholder={t('Header name')}
                                    size="large"
                                    onKeyDown={(e) => {
                                      if (e.key === ' ') {
                                        e.preventDefault();
                                      }
                                    }}
                                    disabled={!isEditingPrimaryLanguage}
                                  />
                                </Form.Item>
                              </Col>

                              <Col sm={12} xs={24}>
                                <Form.Item className="m-0">
                                  <Row wrap={false} align="bottom" className="w-100">
                                    <Col flex={'auto'}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'variable']}
                                        className="mb-0 hide-error"
                                      >
                                        {
                                          storyVariables && responseHeadersVariables ? (
                                            <Select
                                              showSearch
                                              size="large"
                                              disabled={!isEditingPrimaryLanguage}
                                              placeholder={t('Set to variable')}
                                              getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                              // When the user types a new variable name, add it to the list of local variables
                                              onSearch={updateResponseHeadersVariables}
                                              onBlur={(e) => updateResponseHeadersVariables(e.target.value)}
                                              onSelect={(value) => {
                                                const selectedVariableData = responseHeadersVariables.find(variable => variable.name === value);
                                                if (selectedVariableData?.type) {
                                                  form.setFieldValue(['data', 'httpRequestData', 'responseHeaderMappings', name, 'type'], selectedVariableData.type);
                                                }
                                              }}
                                              loading={isFetchingStoryVariables}
                                              options={(responseHeadersVariables || [])
                                                .filter((variable) => {
                                                  return ![VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.CSAT].includes(variable.type);
                                                })
                                                .map((element) => {
                                                  return {
                                                    value: element.name || element.variable,
                                                    label: element.name
                                                  };
                                                })
                                                // Keep only unique values
                                                .filter((elem, index, self) => self.findIndex((t) => {
                                                  return (t.value === elem.value);
                                                }) === index)
                                              }
                                            />
                                          ) : null
                                        }
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col flex="none">
                            <Button
                              icon={<DeleteIcon />}
                              size="large"
                              type="text"
                              onClick={() => remove(name)}
                              disabled={!isEditingPrimaryLanguage}
                            />
                          </Col>
                        </Row>
                      </Form.Item>
                    ))
                  }
                </div>

                <Row gutter={16}>
                  <Col sm={12} xs={24}>
                    <Button
                      type="dashed"
                      size="large"
                      block
                      onClick={() => add({ path: '', variable: undefined, type: 'SESSION' })}
                      icon={<PlusIcon />}
                      disabled={!isEditingPrimaryLanguage}
                    >
                      {t('Add new mapping')}
                    </Button>
                  </Col>
                </Row>

                <Form.ErrorList errors={errors} />
              </Fragment>
            )}

          </Form.List>
        </Col>
      </Row>

    </Fragment>
  );
};

HttpRequestNodeForm.propTypes = {
  form: PropTypes.object
};

export default HttpRequestNodeForm;
